import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, LogOut, Shield, Compass, ChevronDown } from 'lucide-react';

import { paths } from '../../routes/paths';
import { logout } from '../../store/slices/auth';
import { tourReplayed } from '../../store/slices/tour';
import { selectUser } from '../../store/selectors/auth';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Avatar, AvatarImage, AvatarFallback } from '../../ui/Avatar';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
} from '../../ui/DropdownMenu';

const titleFor = (pathname) => {
  if (pathname.startsWith('/dashboard/session/audio/queue')) return 'Session · Review queue';
  if (pathname.startsWith('/dashboard/content/taxonomy/queue')) return 'Content · Review queue';
  if (pathname.startsWith('/dashboard/plan/activity/queue')) return 'Plan · Review queue';
  if (pathname.startsWith('/dashboard/session/audio/new')) return 'New Audio';
  if (pathname.includes('/dashboard/session/audio/') && pathname.endsWith('/edit'))
    return 'Edit Audio';
  if (pathname.startsWith('/dashboard/session/audio')) return 'Session · Audio';
  if (pathname.startsWith('/dashboard/audio')) return 'Asset Manager · Audio';
  if (pathname.startsWith('/dashboard/images')) return 'Asset Manager · Image';
  if (pathname.startsWith('/dashboard/videos')) return 'Asset Manager · Video';
  if (pathname.startsWith('/dashboard/team')) return 'Team';
  if (pathname.startsWith('/dashboard/security')) return 'Security';
  if (pathname.startsWith('/dashboard/review')) return 'Review overview';
  if (pathname.startsWith('/dashboard/activity-log')) return 'Activity log';
  if (pathname.includes('/dashboard/customers/') && pathname.endsWith('/insights'))
    return 'Customer · Insights';
  if (pathname.startsWith('/dashboard/customers')) return 'Customers';
  if (pathname.startsWith('/dashboard/biosignal/observations')) return 'Biosignal · Observations';
  if (pathname.startsWith('/dashboard/biosignal/metrics')) return 'Biosignal · Night Metrics';
  if (pathname.startsWith('/dashboard/biosignal')) return 'Biosignal · Device Syncs';
  if (pathname.startsWith('/dashboard/devices/links')) return 'Devices · User links';
  if (pathname.startsWith('/dashboard/devices')) return 'Devices · Fleet';
  if (pathname.startsWith('/dashboard/firmware/queue')) return 'Devices · Review queue';
  if (pathname.startsWith('/dashboard/firmware/logs')) return 'Devices · Update logs';
  if (pathname.startsWith('/dashboard/firmware/rollbacks')) return 'Devices · Rollback requests';
  if (pathname.startsWith('/dashboard/firmware')) return 'Devices · Firmware releases';
  if (pathname.startsWith('/dashboard/content/taxonomy')) return 'Content · Taxonomy';
  if (pathname.startsWith('/dashboard/content/attributes')) return 'Content · Attributes';
  if (pathname.startsWith('/dashboard/plan/activity')) return 'Plan · Activity';
  if (pathname.startsWith('/dashboard/coach/activity-groups')) return 'Plan · Activity groups';
  if (pathname.startsWith('/dashboard/subscriptions/entitlements'))
    return 'Subscriptions · Entitlements';
  if (pathname.startsWith('/dashboard/subscriptions/plans')) return 'Subscriptions · Plans';
  if (pathname.startsWith('/dashboard/notifications/framework')) return 'Notification framework';
  if (pathname.startsWith('/dashboard/notifications')) return 'Notifications';
  if (pathname.startsWith('/dashboard/app-versions')) return 'App versions';
  if (pathname.startsWith('/dashboard/cache')) return 'Cache';
  if (pathname.startsWith('/dashboard/assessments')) return 'Assessments';
  if (pathname.startsWith('/dashboard/profile')) return 'My profile';
  if (pathname.startsWith('/dashboard/cv/placement-tester'))
    return 'CV Pipeline · Placement Tester';
  return 'Dashboard';
};

export const Topbar = ({ onMenuClick }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((p) => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : 'U';

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-border bg-background px-4 shadow-sm md:px-6">
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Open menu"
          data-tour="sidebar"
          onClick={onMenuClick}
          className="-ml-2 flex h-11 w-11 items-center justify-center rounded-full text-icon-color hover:bg-item-hover lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h1 className="truncate text-lg text-foreground">{titleFor(pathname)}</h1>
      </div>
      <div className="flex items-center gap-3">
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              data-tour="menu"
              className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-item-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Avatar className="h-8 w-8">
                {user.picture && <AvatarImage src={user.picture} alt={user.name || 'User'} />}
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden flex-col text-left sm:flex">
                <span className="text-sm font-medium text-foreground">{user.name || 'User'}</span>
                {user.phone && <span className="text-xs text-gray-dark">{user.phone}</span>}
              </div>
              <ChevronDown className="h-4 w-4 text-gray-dark" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">{user.name || 'User'}</span>
                  {user.phone && (
                    <span className="text-xs font-normal text-gray-dark">{user.phone}</span>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(paths.dashboard.security)}>
                <Shield className="h-4 w-4" />
                <span>Security</span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => dispatch(tourReplayed())}>
                <Compass className="h-4 w-4" />
                <span>Show the tour again</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-danger-foreground">
                <LogOut className="h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <button
            type="button"
            onClick={handleLogout}
            className="rounded border border-border bg-background px-3 py-1.5 text-sm text-gray-dark shadow-sm hover:bg-item-hover"
          >
            Logout
          </button>
        )}
      </div>
    </header>
  );
};
